import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "@apollo/client/react";
import { PinGroupMembershipsDocument } from "@/graphql/generated/operations";
import { SaveGroupPopover } from "@/components/save/SaveCollectionTemplate";
import type { PinGroupLite } from "@/components/save/PinGroupSidebar";

// Group-assign pill. A pin lives in at most ONE group (single-group, unlike
// Community contacts), so the popover is a move/assign: the current group is
// marked with a check (click it to ungroup), clicking another moves the pin
// there. The trigger label reflects state: "Verschieben" when the pin is
// already in a group, "+ Hinzufügen" when it is in none. Membership is loaded
// eagerly because the closed label depends on it.
export function AddToPinGroup({
  itemId,
  isPerson,
  options,
  onAdd,
  onRemoveGroup,
  glass = false,
}: {
  itemId: string;
  isPerson: boolean;
  options: PinGroupLite[];
  onAdd: (groupId: string) => Promise<void> | void;
  onRemoveGroup: (groupId: string) => Promise<void> | void;
  // Glass treatment for the floating overlay over cover images (Posts/Projekte);
  // default is the quiet grey chip used on the light person card (matches Community).
  glass?: boolean;
}) {
  const intl = useIntl();
  // Group id whose add/remove is in flight, for a per-pill spinner.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { data } = useQuery(PinGroupMembershipsDocument, {
    variables: { input: isPerson ? { personId: itemId } : { sharedItemId: itemId } },
    fetchPolicy: "cache-and-network",
  });
  const memberOut = data?.pinGroupMemberships;
  const memberIds = new Set<string>(
    memberOut?.__typename === "PinGroupMembershipResult"
      ? memberOut.pinGroups.map((g) => g.id)
      : [],
  );

  async function toggle(groupId: string, isMember: boolean) {
    if (pendingId) return;
    setPendingId(groupId);
    try {
      await (isMember ? onRemoveGroup(groupId) : onAdd(groupId));
    } finally {
      setPendingId(null);
    }
  }

  if (options.length === 0) return null;
  return (
    <SaveGroupPopover
      idPrefix={`save-card-${itemId}-group`}
      triggerLabel={`+ ${intl.formatMessage({ id: "common.add", defaultMessage: "Add" })}`}
      options={options}
      memberIds={memberIds}
      pendingId={pendingId}
      glass={glass}
      onToggle={(groupId, isMember) => void toggle(groupId, isMember)}
    />
  );
}
